import {
  useCallback,
  useEffect
} from 'react'
import {
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {
  getStayOptionsApiV1StaysOptionsTokenGet
} from '../client'
import type {
  StayOptionSetResponse
} from '../client'
import {
  StayEvents,
  trackEvent
} from '../analytics/posthog'
import {
  API_BASE_URL
} from '../config'
import {
  getApiErrorStatus
} from '../services/api'
import {
  optionSetLoadFailure
} from '../utils/optionSetLoad'
import type {
  OptionSetLoadFailure
} from '../utils/optionSetLoad'

export type StayLoadState = 'loading' | OptionSetLoadFailure | 'ready'

class OptionSetLoadError extends Error {
  constructor(readonly failure: OptionSetLoadFailure, readonly status: number | undefined) {
    super(`Stay option set ${failure}`)
  }
}

function optionSetKey(token: string | undefined): readonly unknown[] {
  return ['stay-option-set', token]
}

async function fetchOptionSet(token: string): Promise<StayOptionSetResponse> {
  const res = await getStayOptionsApiV1StaysOptionsTokenGet({
    path: { token },
    baseURL: API_BASE_URL,
    throwOnError: false
  })
  if (res.error || !res.data) {
    const status = getApiErrorStatus(res)
    throw new OptionSetLoadError(optionSetLoadFailure(status), status)
  }
  return res.data
}

/**
 * The option set behind /stays/:token and its confirmation page, cached so
 * moving between the two does not fetch it again: the GET is rate-limited per
 * IP, and a refetch shows a loading screen in between.
 */
export function useStayOptionSet(token: string | undefined, page: 'list' | 'confirm'): {
  state: StayLoadState
  data: StayOptionSetResponse | undefined
  retry: () => void
  markSelected: () => void
} {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: optionSetKey(token),
    queryFn: () => fetchOptionSet(token as string),
    enabled: Boolean(token),
    // Set here rather than inherited from the app's QueryClient: the list ->
    // confirm -> back round trip depends on it. A set changes only when a pick
    // is made, which `markSelected` writes into the cache and the POST's 409
    // catches from elsewhere.
    staleTime: 5 * 60 * 1000,
    // One attempt; the page offers its own retry, and a 404 means the set expired.
    retry: false,
    // A refetch mid-confirmation would re-arm "Yes, book it" and log the view twice.
    refetchOnWindowFocus: false
  })
  const failure = query.error instanceof OptionSetLoadError ? query.error : null

  useEffect(() => {
    if (failure) {
      trackEvent(StayEvents.OPTIONS_UNAVAILABLE, {
        token, failure: failure.failure, status: failure.status, page
      })
    }
  }, [failure, token, page])

  // Once a pick is made, both pages read the set as taken without a refetch.
  const markSelected = useCallback(() => {
    queryClient.setQueryData<StayOptionSetResponse>(
      optionSetKey(token),
      (set) => set && { ...set, already_selected: true }
    )
  }, [queryClient, token])

  const { refetch } = query
  const retry = useCallback(() => {
    void refetch()
  }, [refetch])

  let state: StayLoadState
  if (!token) {
    state = 'expired'
  } else if (query.data) {
    state = 'ready'
  } else if (query.isFetching || query.isPending) {
    state = 'loading'
  } else {
    state = failure?.failure ?? 'unreachable'
  }
  return { state, data: query.data, retry, markSelected }
}
